import React from "react";
import { FormikErrors, FormikProps, withFormik } from "formik";
import { Button } from "@chakra-ui/button";
import { HStack } from "@chakra-ui/layout";

import { AdminApiClient } from "../api/adminApiClient";
import { CherryHubAdminApi } from "../api/adminApiModels";
import { TextField } from "../components/fields/TextField/TextField";
import { isEmptyStr } from "../util/stringHelper";
import { FormStack } from "../components/FormStack/FormStack";
import { SwitchField } from "../components/fields/SwitchField/SwitchField";

const emptyStrToNull = (value: string | null): string | null => {
  if (!value || value.trim() === "") {
    return null;
  } else {
    return value;
  }
};

interface NewDigitalMembershipCardPassConfigFormValues {
  displayName: string;
  passDescription: string;
  isMembershipApplicationPass?: boolean;
}

interface NewDigitalMembershipCardPassConfigFormProps {
  apiClient: AdminApiClient;
  business: CherryHubAdminApi.Business;
  isDisabled?: boolean;
  onCancel: () => void;
  onSuccess?: (
    venue?: CherryHubAdminApi.CherryPassConfigurationSummaryItem
  ) => void;
  onFailure?: (message?: string) => void;
}

const InnerForm = ({
  isSubmitting,
  isDisabled,
  submitForm,
  isValid,
  onCancel,
  values,
}: NewDigitalMembershipCardPassConfigFormProps &
  FormikProps<NewDigitalMembershipCardPassConfigFormValues>) => {
  return (
    <FormStack>
      <TextField label="Display Name" name="displayName" />
      <TextField label="Pass Description" name="passDescription" />
      <SwitchField
        label="Membership Application Pass"
        name="isMembershipApplicationPass"
      />

      <HStack width="100%" justifyContent="end" spacing="3" pt="8">
        <Button
          colorScheme="cherryButton"
          type="submit"
          isLoading={isSubmitting}
          disabled={isSubmitting || !isValid}
          onClick={submitForm}
        >
          Save
        </Button>
        <Button disabled={isSubmitting} onClick={onCancel}>
          Cancel
        </Button>
      </HStack>
    </FormStack>
  );
};

export const NewDigitalMembershipCardPassConfigForm = withFormik<
  NewDigitalMembershipCardPassConfigFormProps,
  NewDigitalMembershipCardPassConfigFormValues
>({
  handleSubmit: async (values, { props, setFieldError }) => {
    if (isEmptyStr(values.displayName)) {
      setFieldError("displayName", "Display name is required.");
      return;
    }

    // Translate form state to CherryPassConfigurationSummaryItem
    const configDoc: Partial<
      CherryHubAdminApi.CherryPassConfigurationSummaryItem & {
        isMembershipApplicationPass?: boolean;
      }
    > = {
      DisplayName: values.displayName,
      PassDescription: values.passDescription,
      ConfigurationType: "DigitalMembershipCard",
      isMembershipApplicationPass: values.isMembershipApplicationPass,
    };

    let result = await props.apiClient.createPassConfiguration(
      props.business.BusinessId as string,
      configDoc
    );

    if (result.ok) {
      if (props.onSuccess) {
        props.onSuccess(result.data);
      }
    } else {
      if (props.onFailure) {
        props.onFailure(result.message);
      }
    }
  },

  validateOnBlur: false,
  validateOnMount: false,
  validateOnChange: true,

  validate: (values) => {
    let errors: FormikErrors<NewDigitalMembershipCardPassConfigFormValues> = {};

    if (isEmptyStr(values.displayName)) {
      errors.displayName = "Display name is required.";
    }

    if (isEmptyStr(values.passDescription)) {
      errors.passDescription = "Pass Description is required.";
    }

    return errors;
  },
})(InnerForm);
